import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { PenSquare } from "lucide-react";

export const CreateNews = () => {
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(false);

  // Onglet langue courant (FR par défaut)
  const [activeLang, setActiveLang] = useState<"fr" | "en">("fr");

  // Champs FR
  const [titleFr, setTitleFr] = useState("");
  const [contentFr, setContentFr] = useState("");

  // Champs EN
  const [titleEn, setTitleEn] = useState("");
  const [contentEn, setContentEn] = useState("");

  const [submitting, setSubmitting] = useState(false);

  const adminEmail = "cyprien.conseillon@icloud.com";

  useEffect(() => {
    const checkAccess = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || user.email !== adminEmail) {
        navigate("/");
      } else {
        setAuthorized(true);
      }
    };
    checkAccess();
  }, [navigate]);

  const handleSubmit = async () => {
    const hasFr = titleFr.trim() && contentFr.trim();
    const hasEn = titleEn.trim() && contentEn.trim();

    if (!hasFr && !hasEn) {
      alert("Renseigne au moins le titre et contenu en FR ou EN.");
      return;
    }

    // Champs legacy pour compat : on privilégie FR si dispo, sinon EN
    const legacyTitle = hasFr ? titleFr.trim() : titleEn.trim();
    const legacyContent = hasFr ? contentFr.trim() : contentEn.trim();

    setSubmitting(true);
    const { error } = await supabase.from("news").insert([{
      // legacy (toujours présents dans la table aujourd'hui)
      title: legacyTitle,
      content: legacyContent,

      // nouveaux champs multilingues
      title_fr: titleFr.trim() || null,
      content_fr: contentFr.trim() || null,
      title_en: titleEn.trim() || null,
      content_en: contentEn.trim() || null
    }]);
    setSubmitting(false);

    if (error) {
      console.error("Erreur Supabase :", error);
      alert("Erreur lors de la publication.");
    } else {
      alert("Article publié !");
      // reset
      setTitleFr(""); setContentFr("");
      setTitleEn(""); setContentEn("");
      // retour à la liste
      navigate("/news");
    }
  };

  if (!authorized) return null;

  const isDisabled =
    submitting ||
    (!titleFr.trim() && !titleEn.trim()) ||
    (!contentFr.trim() && !contentEn.trim());

  return (
    <section className="min-h-screen w-full p-6 mx-auto bg-gray-50 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-bold mb-6 text-center text-orange-600 dark:text-orange-400 flex items-center justify-center gap-2">
          <PenSquare className="w-6 h-6" />
          Ajouter une News (FR / EN)
        </h1>

        <div className="rounded-2xl border bg-white border-gray-200 shadow-sm p-6 dark:bg-gray-900 dark:border-gray-800">
          {/* Onglets langue */}
          <div className="inline-flex rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 mb-5">
            <button
              type="button"
              onClick={() => setActiveLang("fr")}
              className={`px-4 py-2 text-sm font-medium ${
                activeLang === "fr"
                  ? "bg-orange-500 text-white"
                  : "bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200"
              }`}
            >
              Français
            </button>
            <button
              type="button"
              onClick={() => setActiveLang("en")}
              className={`px-4 py-2 text-sm font-medium border-l border-gray-200 dark:border-gray-700 ${
                activeLang === "en"
                  ? "bg-orange-500 text-white"
                  : "bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200"
              }`}
            >
              English
            </button>
          </div>

          {/* Form FR */}
          {activeLang === "fr" && (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1 text-gray-800 dark:text-gray-200">
                  Titre (FR)
                </label>
                <input
                  type="text"
                  value={titleFr}
                  onChange={(e) => setTitleFr(e.target.value)}
                  placeholder="Titre en français"
                  className="w-full rounded px-3 py-2 border
                             bg-white border-gray-300 text-gray-900 placeholder-gray-400
                             focus:outline-none focus:ring-2 focus:ring-orange-400/60
                             dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium mb-1 text-gray-800 dark:text-gray-200">
                  Contenu (FR)
                </label>
                <textarea
                  value={contentFr}
                  onChange={(e) => setContentFr(e.target.value)}
                  placeholder="Contenu en français"
                  rows={8}
                  className="w-full rounded px-3 py-2 border
                             bg-white border-gray-300 text-gray-900 placeholder-gray-400
                             focus:outline-none focus:ring-2 focus:ring-orange-400/60
                             dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
                />
              </div>
            </>
          )}

          {/* Form EN */}
          {activeLang === "en" && (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1 text-gray-800 dark:text-gray-200">
                  Title (EN)
                </label>
                <input
                  type="text"
                  value={titleEn}
                  onChange={(e) => setTitleEn(e.target.value)}
                  placeholder="Title in English"
                  className="w-full rounded px-3 py-2 border
                             bg-white border-gray-300 text-gray-900 placeholder-gray-400
                             focus:outline-none focus:ring-2 focus:ring-orange-400/60
                             dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium mb-1 text-gray-800 dark:text-gray-200">
                  Content (EN)
                </label>
                <textarea
                  value={contentEn}
                  onChange={(e) => setContentEn(e.target.value)}
                  placeholder="Content in English"
                  rows={8}
                  className="w-full rounded px-3 py-2 border
                             bg-white border-gray-300 text-gray-900 placeholder-gray-400
                             focus:outline-none focus:ring-2 focus:ring-orange-400/60
                             dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
                />
              </div>
            </>
          )}

          <div className="text-xs text-gray-600 dark:text-gray-400 mb-4">
            Astuce : tu peux publier uniquement en FR ou uniquement en EN — la page d’affichage basculera automatiquement selon la langue de l’utilisateur et utilisera une langue de secours si besoin.
          </div>

          <button
            onClick={handleSubmit}
            disabled={isDisabled}
            className={`w-full font-semibold py-2 px-6 rounded transition
                       focus:outline-none focus:ring-2 focus:ring-orange-400/60
                       ${isDisabled
                          ? "bg-orange-300 cursor-not-allowed text-white"
                          : "bg-[#fc7825] hover:bg-orange-600 text-white"}`}
          >
            {submitting ? "Publication..." : "Publier"}
          </button>
        </div>
      </div>
    </section>
  );
};
